/**
 * V2 Contact Service
 * Submits the v2 Contact form to the real /api/contact endpoint.
 */

import { apiFetch, type ApiError } from "@/lib/api";

export interface ContactPayload {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
  captchaToken: string;
  formStartTime: number;
}

export interface ContactResult {
  success: boolean;
  message: string;
  errors?: Record<string, string>;
}

/**
 * Maps the v2 Select option values (e.g. "general") onto the API-accepted
 * SUBJECT_OPTIONS strings used by `contactFormSubmissionSchema`. Keeps the v2
 * UI copy unchanged while the API receives a value it understands.
 */
const SUBJECT_VALUE_TO_API: Record<string, string> = {
  general: "General Question",
  booking: "Booking Support",
  listing: "Listing Help",
  payment: "Payment Issue",
  dispute: "Safety Concern",
  "trust-safety": "Safety Concern",
  account: "Technical Issue",
  other: "Other",
};

export async function submitContactForm(payload: ContactPayload): Promise<ContactResult> {
  const mappedSubject = SUBJECT_VALUE_TO_API[payload.subject] ?? payload.subject;

  const submissionData = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    subject: mappedSubject,
    message: payload.message,
    timestamp: payload.formStartTime.toString(),
    jsEnabled: "true",
    website: "",
    confirm_email: "",
    captchaToken: payload.captchaToken,
    token: payload.captchaToken,
  };

  try {
    await apiFetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(submissionData),
    });
    return {
      success: true,
      message: "We've received your message and will respond within 24 hours.",
    };
  } catch (err) {
    const apiErr = err as ApiError;
    return {
      success: false,
      message: apiErr.message || "Something went wrong. Please try again.",
      errors:
        apiErr.errors && typeof apiErr.errors === "object" ? apiErr.errors : undefined,
    };
  }
}
